import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./firebase.config";

class OpenAIService {
  /**
   * Generate personality insights based on quiz results
   * @param {Array} quizResults - Array of quiz result objects with percentage1, field, etc.
   * @param {string} quizType - Type of quiz (personality-check, best-date, etc.)
   * @param {string} personName - Name of the person (optional, for shared contexts)
   * @returns {Promise<Object>} - AI-generated insights
   */
  async generatePersonalityInsights(quizResults, quizType, personName = null) {
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error("User must be authenticated to generate insights");
      }

      // Get a fresh auth token to ensure it's valid
      const idToken = await auth.currentUser.getIdToken(true);
      console.log("🔑 Got fresh auth token:", idToken ? "Present" : "Missing");

      // Format quiz results for the Cloud Function
      const formattedResults = quizResults.map((result) => ({
        field: result.field,
        percentage1: result.percentage1,
        percentage2: result.percentage2 || undefined,
      }));

      console.log(
        "🤖 Calling OpenAI insights for:",
        quizType,
        formattedResults
      );
      console.log("🔑 Current user:", auth.currentUser?.uid);
      console.log("🔑 Functions region:", functions.region);
      console.log("🔑 Auth token present:", !!idToken);

      // Create the callable function with explicit auth context
      const generateInsights = httpsCallable(functions, "generateInsights");

      // Call the Firebase Function
      const result = await generateInsights({
        quizResults: formattedResults,
        quizType: quizType,
        personName: personName,
      });

      return {
        success: true,
        insights: result.data.insights,
        timestamp: result.data.timestamp,
      };
    } catch (error) {
      console.error("❌ Error generating insights:", error);

      // Handle specific error types
      if (error.code === "unauthenticated") {
        throw new Error("Please log in to generate personality insights");
      } else if (error.code === "resource-exhausted") {
        throw new Error(
          "AI service is temporarily unavailable. Please try again later."
        );
      } else if (error.code === "invalid-argument") {
        throw new Error("Invalid quiz data provided");
      } else if (error.code === "functions/internal") {
        throw new Error("Server error - please try again in a moment");
      } else {
        throw new Error("Failed to generate insights. Please try again.");
      }
    }
  }

  /**
   * Generate compatibility insights based on two people's quiz results
   * @param {Array} sharedQuizResults - Array with both people's results {field, percentage1, percentage2}
   * @param {string} quizType - Type of quiz (personality-check, best-date, etc.)
   * @param {string} person1Name - Name of first person
   * @param {string} person2Name - Name of second person
   * @returns {Promise<Object>} - AI-generated compatibility insights
   */
  async generateCompatibilityInsights(
    sharedQuizResults,
    quizType,
    person1Name = "Person 1",
    person2Name = "Person 2"
  ) {
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error("User must be authenticated to generate insights");
      }

      // Get a fresh auth token to ensure it's valid
      const idToken = await auth.currentUser.getIdToken(true);
      console.log(
        "🔑 Got fresh auth token for compatibility:",
        idToken ? "Present" : "Missing"
      );

      // Format shared results for the Cloud Function
      const formattedResults = sharedQuizResults.map((result) => ({
        field: result.field,
        percentage1: result.percentage1,
        percentage2: result.percentage2,
      }));

      console.log(
        "💕 Calling compatibility insights for:",
        quizType,
        formattedResults
      );

      // Create the callable function with explicit auth context
      const generateCompatibility = httpsCallable(
        functions,
        "generateCompatibilityInsights"
      );

      // Call the Firebase Function
      const result = await generateCompatibility({
        sharedQuizResults: formattedResults,
        quizType: quizType,
        person1Name: person1Name,
        person2Name: person2Name,
      });

      return {
        success: true,
        insights: result.data.insights,
        timestamp: result.data.timestamp,
      };
    } catch (error) {
      console.error("❌ Error generating compatibility insights:", error);

      // Handle specific error types
      if (error.code === "unauthenticated") {
        throw new Error("Please log in to generate compatibility insights");
      } else if (error.code === "resource-exhausted") {
        throw new Error(
          "AI service is temporarily unavailable. Please try again later."
        );
      } else if (error.code === "invalid-argument") {
        throw new Error("Invalid quiz data provided");
      } else if (error.code === "functions/internal") {
        throw new Error("Server error - please try again in a moment");
      } else {
        throw new Error(
          "Failed to generate compatibility insights. Please try again."
        );
      }
    }
  }

  /**
   * Generate comprehensive profile insights based on all completed quizzes
   * @param {Array} allQuizData - Array of quiz data objects with {quizType, results, insights}
   * @param {string} personName - Name of the person
   * @returns {Promise<Object>} - AI-generated comprehensive profile insights
   */
  async generateProfileInsights(allQuizData, personName = "this person") {
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error("User must be authenticated to generate insights");
      }

      // Get a fresh auth token to ensure it's valid
      const idToken = await auth.currentUser.getIdToken(true);
      console.log(
        "🔑 Got fresh auth token for profile insights:",
        idToken ? "Present" : "Missing"
      );

      // Format all quiz data for the Cloud Function
      const formattedQuizData = allQuizData.map((quiz) => ({
        quizType: quiz.quizType,
        quizName: quiz.quizName,
        results: quiz.results.map((result) => ({
          field: result.field,
          percentage1: result.percentage1,
        })),
        existingInsights: quiz.insights,
      }));

      console.log(
        "🧠 Calling profile insights for:",
        personName,
        "with",
        formattedQuizData.length,
        "quizzes"
      );

      // Create the callable function with explicit auth context
      const generateProfile = httpsCallable(
        functions,
        "generateProfileInsights"
      );

      // Call the Firebase Function
      const result = await generateProfile({
        allQuizData: formattedQuizData,
        personName: personName,
      });

      return {
        success: true,
        insights: result.data.insights,
        timestamp: result.data.timestamp,
      };
    } catch (error) {
      console.error("❌ Error generating profile insights:", error);

      // Handle specific error types
      if (error.code === "unauthenticated") {
        throw new Error("Please log in to generate profile insights");
      } else if (error.code === "resource-exhausted") {
        throw new Error(
          "AI service is temporarily unavailable. Please try again later."
        );
      } else if (error.code === "invalid-argument") {
        throw new Error("Invalid quiz data provided");
      } else if (error.code === "functions/internal") {
        throw new Error("Server error - please try again in a moment");
      } else {
        throw new Error(
          "Failed to generate profile insights. Please try again."
        );
      }
    }
  }

  /**
   * Check if insights have already been generated for this quiz
   * @param {string} userId - User ID
   * @param {string} quizType - Type of quiz
   * @returns {Promise<Object|null>} - Cached insights or null
   */
  async getCachedInsights(userId, quizType) {
    // This would typically check Firestore for cached insights
    // For now, we'll let the Cloud Function handle caching
    return null;
  }
}

// Create and export a singleton instance
const openAIService = new OpenAIService();
export default openAIService;
